/**
 * Google-hosted font families with their supported weights.
 * Generated from the Google Fonts CSS API (v1) — update when adding new fonts.
 * Single-weight fonts (e.g. Parisienne, Hurricane) only support 400;
 * requesting any other weight returns HTTP 400 and breaks rendering.
 */
// prettier-ignore
const GOOGLE_HOSTED: Readonly<Record<string, readonly number[]>> = {
  'Antic Didone':        [400],
  'Cinzel':              [400, 500, 600, 700, 800, 900],
  'Cormorant Garamond':  [300, 400, 500, 600, 700],
  'Crimson Text':        [400, 600, 700],
  'DM Serif Display':    [400],
  'EB Garamond':         [400, 500, 600, 700, 800],
  'Gilda Display':       [400],
  'Homemade Apple':      [400],
  'Hurricane':           [400],
  'Jost':                [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'Literata':            [200, 300, 400, 500, 600, 700, 800, 900],
  'Lora':                [400, 500, 600, 700],
  'Monsieur La Doulaise':[400],
  'Montserrat':          [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'Mr De Haviland':      [400],
  'Oswald':              [200, 300, 400, 500, 600, 700],
  'Parisienne':          [400],
  'Pinyon Script':       [400],
  'Playfair Display':    [400, 500, 600, 700, 800, 900],
  'Playfair Display SC': [400, 700, 900],
  'Protest Riot':        [400],
  'Reem Kufi':           [400, 500, 600, 700],
  'Roboto':              [100, 200, 300, 400, 500, 600, 700, 800, 900],
  'Seaweed Script':      [400],
  'Spectral':            [200, 300, 400, 500, 600, 700, 800],
};

/**
 * Given a font family and a requested weight, return the closest weight that
 * Google Fonts actually supports. Returns the weight as-is for self-hosted or
 * unknown families. Used both when loading fonts (so we never request e.g.
 * "Parisienne:700", which returns HTTP 400) and when rendering card text (so
 * the CSS weight matches a loaded face and the browser never synthesizes bold).
 */
export function clampToSupportedWeight(family: string, weight: number): number {
  const supported = GOOGLE_HOSTED[family];
  if (!supported) return weight;
  let closest = supported[0];
  for (let i = 1; i < supported.length; i++) {
    if (Math.abs(supported[i] - weight) < Math.abs(closest - weight)) {
      closest = supported[i];
    }
  }
  return closest;
}

// Self-hosted fonts are single-weight files — the browser synthesizes other
// weights from the one face we provide, so no weight restriction applies.
// prettier-ignore
export const SELF_HOSTED: Readonly<Record<string, string>> = {
  'Adelicia Script Clean':            'Adelicia Script Clean.woff2',
  'Adorable':                         'Adorable-Line.woff', // Only supporting the style "line"
  'Allenoire':                        'Allenoire.woff2',
  'Bodoni 72':                        'Bodoni 72.woff',
  'Bon Vivant':                       'BonVivant.woff2',
  'Bon Vivant Serif':                 'BonVivantSerif.woff2',
  'Bon Vivant Serif Bold':            'BonVivantSerifBold.woff2',
  'Brand':                            'Brand.woff',
  'California Palms Script Thicker':  'CaliforniaPalms_Script_thicker.woff2',
  'Casablanca':                       'Casablanca.woff2',
  'Dream Avenue':                     'Dream Avenue.woff',
  'Hopeless Romantic Society':        'Hopeless Romantic Society.woff2',
  'Losta Masta':                      'Losta Masta.woff',
  'Lunaria Celesttial':               'Lunaria Celesttial.woff',
  'Mozart Script':                    'MozartScript-Regular.woff',
  'Nostalgia':                        'Nostalgia.woff2',
  'Paris Script':                     'Paris Script.woff2',
  'Parisian Script':                  'Parisian Script.woff',
  'Silk Serif':                       'SilkSerif-ExtraLight.woff2',
  'Slight':                           'slight-regular_2-webfont.woff2',
  'Titania':                          'Titania-Regular.woff'
};

// Not user selectable, but used behind the scenes.
// prettier-ignore
export const SELF_HOSTED_SYSTEM: Readonly<Record<string, string>> = {
  'Adobe NotDef': 'Adobe NotDef.otf'
}

// Deprecated families: still loaded and rendered for existing designs that
// reference them, but hidden from the font picker so nobody can newly pick one.
const DEPRECATED_FAMILIES: ReadonlySet<string> = new Set(['Bon Vivant', 'Bon Vivant Serif', 'Bon Vivant Serif Bold', 'California Palms Script Thicker']);

export const FONT_FAMILIES = [...Object.keys(GOOGLE_HOSTED), ...Object.keys(SELF_HOSTED)].filter(family => !DEPRECATED_FAMILIES.has(family)).sort();

export function isDeprecatedFamily(family: string): boolean {
  return DEPRECATED_FAMILIES.has(family);
}

/**
 * Font picker options for a layer currently set to `currentFamily`. A design
 * still using a deprecated family keeps it in the list — selected, scrolled to,
 * and disabled — so the picker reflects reality without letting anyone newly
 * pick it. Any other family yields the plain selectable list.
 */
export function getFontFamilyOptions(currentFamily: string): ReadonlyArray<{ family: string; disabled: boolean }> {
  const options = FONT_FAMILIES.map(family => ({ family, disabled: false }));
  if (!isDeprecatedFamily(currentFamily)) return options;
  // Sort with the same default UTF-16 comparator FONT_FAMILIES uses, so the
  // list order does not shift depending on whether a deprecated font is
  // currently selected.
  return [...options, { family: currentFamily, disabled: true }].sort((a, b) => (a.family < b.family ? -1 : a.family > b.family ? 1 : 0));
}
